import React, {useEffect, useRef} from "react";
import {useFormik} from "formik";
import {formatISO, parseISO} from "date-fns";
import * as yup from "yup";

import {ResponsiveDialog} from "../../../components/ResponsiveDialog";
import {useMainLayout} from "../../../layouts/Main";

import PeopleIcon from "@mui/icons-material/People";
import EuroIcon from "@mui/icons-material/Euro";
import NightsStayIcon from '@mui/icons-material/NightsStay';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import Box from "@mui/material/Box";
import TabBasePrice from "./TabBasePrice";
import TabOccupancy from "./TabOccupancy";
import TabArrivalDeparture, {dayAttributeNames} from "./TabArrivalDeparture";
import TabMinNights from "./TabMinNights";
import {INDETERMINATE} from "./usePriceSummarizer";
import {useUpdatePricesMutation} from "../query";
import {useFeedbackTools} from "../../../components/FeedbackTools";

export const BASE_TAB = "base";
export const OCCUPANCY_TAB = "occupancy";
export const ARRIVAL_DEPARTURE_TAB = "arrivalDeparture";
export const NIGHTS_TAB = "nights";


const tabs = [
    {value: BASE_TAB, label: 'Base price', icon: <EuroIcon/>},
    {value: OCCUPANCY_TAB, label: 'Occupancy', icon: <PeopleIcon/>},
    {value: ARRIVAL_DEPARTURE_TAB, label: 'Arrival/Departure', icon: <SyncAltIcon/>},
    {value: NIGHTS_TAB, label: 'Min nights', icon: <NightsStayIcon/>},
]

const filterNullAndIndeterminate = (obj, initialValues) => {
    return Object.keys(obj).reduce((acc, key) => {
        if (typeof obj[key] === "object" && obj[key] !== null) {
            const compareWith = key in initialValues && typeof initialValues[key] === "object" ? initialValues[key] : {};
            const filtered = filterNullAndIndeterminate(obj[key], compareWith);
            if (Object.keys(filtered).length > 0){
                acc[key] = filtered;
            }
        } else if (obj[key] !== null && obj[key] !== INDETERMINATE && obj[key] !== '') {
            if(obj[key] !== initialValues[key] || initialValues[key] === undefined){
                acc[key] = obj[key];
            }
        }
        return acc;
    }, {});
}

const schemaOrIndeterminate = (validationSchema) => {
    return yup.lazy(value => value === INDETERMINATE ?  yup.string() : validationSchema);
}

const positiveNumberSchema = yup.number()
    .typeError('Deve essere un numero')
    .positive('Deve essere un numero positivo')
    .integer('Deve essere un numero intero')
    .nullable();

const booleanOrIndeterminate = (value) => (value === true || value === false) ? value : INDETERMINATE;
const valueOrIndeterminate = (value) => (value !== null && value !== undefined) ? value : INDETERMINATE;

const FormRate = (props) => {
    const {
        onClose,
        rate,
        apartmentId,
        dateFromISO,
        dateToISO,
        initialValues,
        ratesViewMode,
        tab,
        onRatesViewModeChange,
        dateDisabled=true
    } = props;

    const maxOccupancy = rate.maxPeople;

    const {isMobile} = useMainLayout();
    const {showErrorNotification} = useFeedbackTools();
    const recomputePrices = useRef(false);

    // Mutations
    const mutation = useUpdatePricesMutation({apartmentId});

    const occupancies = Array.from(Array(maxOccupancy)).map((_, index) => maxOccupancy - index)
    const formik = useFormik({

        /*
         * The initial values are set in the following way:
         * - they are set to the initial values passed as props if they are valid values
         * - they are set to the INDETERMINATE otherwise
         * This way if we do not pass any initial values, or we pass only some of them, the form will be submitted with
         * the only values changed by the user. The form can therefore used with a selection of dates picked
         * by the user in the planner or with a bulk update.
         */
        initialValues: {
            dateFrom: dateFromISO ? parseISO(dateFromISO) : null,
            dateTo: dateToISO ? parseISO(dateToISO) : null,
            pricesCent: occupancies.reduce((acc, people) => {
                acc[people] = valueOrIndeterminate(initialValues?.pricesCent?.[people]);
                return acc;
            }, {}),
            checkin: {
                monday: booleanOrIndeterminate(initialValues?.checkin?.monday),
                tuesday: booleanOrIndeterminate(initialValues?.checkin?.tuesday),
                wednesday: booleanOrIndeterminate(initialValues?.checkin?.wednesday),
                thursday: booleanOrIndeterminate(initialValues?.checkin?.thursday),
                friday: booleanOrIndeterminate(initialValues?.checkin?.friday),
                saturday: booleanOrIndeterminate(initialValues?.checkin?.saturday),
                sunday: booleanOrIndeterminate(initialValues?.checkin?.sunday),
            },
            checkout: {
                monday: booleanOrIndeterminate(initialValues?.checkout?.monday),
                tuesday: booleanOrIndeterminate(initialValues?.checkout?.tuesday),
                wednesday: booleanOrIndeterminate(initialValues?.checkout?.wednesday),
                thursday: booleanOrIndeterminate(initialValues?.checkout?.thursday),
                friday: booleanOrIndeterminate(initialValues?.checkout?.friday),
                saturday: booleanOrIndeterminate(initialValues?.checkout?.saturday),
                sunday: booleanOrIndeterminate(initialValues?.checkout?.sunday),
            },
            minNights: valueOrIndeterminate(initialValues?.minNights),
            minPeople: valueOrIndeterminate(initialValues?.minPeople),
            enabled: booleanOrIndeterminate(initialValues?.enabled),
        },
        validationSchema: yup.object().shape({
            dateFrom: yup.date().required("Campo obbligatorio"),
            dateTo: yup.date().required("Campo obbligatorio"),
            minNights: schemaOrIndeterminate(positiveNumberSchema),
            minPeople: schemaOrIndeterminate(positiveNumberSchema.min(rate.minPeople, `Deve essere maggiorne di ${rate.minPeople} (vedi piano tariffario)`)),
            enabled: schemaOrIndeterminate(yup.boolean().nullable()),
            pricesCent: yup.object().shape(
                occupancies.reduce((acc, people) => {
                    acc[people] = schemaOrIndeterminate(positiveNumberSchema);
                    return acc;
                }, {})
            ),
            checkin: yup.object().shape(
                dayAttributeNames.reduce((acc, day) => {
                    acc[day] = schemaOrIndeterminate(yup.boolean().nullable());
                    return acc;
                }, {})
            ),
            checkout: yup.object().shape(
                dayAttributeNames.reduce((acc, day) => {
                    acc[day] = schemaOrIndeterminate(yup.boolean().nullable())
                    return acc;
                }, {})
            )
        }),
        onSubmit: async (values) => {
            const {dateFrom, dateTo, ...toFilter} = values;

            // filter the values not changed or indeterminate
            const filtered = filterNullAndIndeterminate(toFilter, initialValues);
            const {pricesCent, ...data} = filtered;
            data.dateFrom = formatISO(dateFrom, {representation: 'date'});
            data.dateTo = formatISO(dateTo, {representation: 'date'});
            if (!!pricesCent){
                data.prices = Object.entries(pricesCent).reduce((acc, [people, priceCent]) => {
                    acc.push({people: people, priceCent});
                    return acc;
                }, [])
            }

            try{
                await mutation.mutateAsync({rateId: rate.id, data: data});
                onClose();
            } catch (e) {
                showErrorNotification(e);
            }
        }
    });

    const initialTab = tabs.find(t => t.value === tab)?.value || BASE_TAB;
    const [tabValue, setTabValue] = React.useState(initialTab);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // When the base price is changed, the prices of the other occupancies are updated
    const basePrice = formik.values.pricesCent[rate.basePeople];
    useEffect(() => {
        // recomputePrices is used to avoid the effect to be executed when the component is mounted
        // otherwise it will overwrite the initial values.
        if(!recomputePrices.current) {
            recomputePrices.current = true;
            return;
        }
        if (rate.priceVariations) {
            const basePriceCent = Number(formik.values.pricesCent[rate.basePeople]);
            const newPricesCent = rate.priceVariations.filter(v => v.people !== rate.basePeople).reduce((acc, v) => {
                if(basePriceCent > 0){
                    acc[v.people] = Math.round(basePriceCent * (1 + v.percentage / 100) / 100 ) * 100;
                }else{
                    // if the base price is not a positive number (empty or indeterminate) the other prices are set to null
                    acc[v.people] = null;
                }
                return acc;
            }, {})
            formik.setFieldValue(`pricesCent`, {...formik.values.pricesCent, ...newPricesCent});
        }
    }, [basePrice]); // eslint-disable-line react-hooks/exhaustive-deps

    return <TabContext value={tabValue}>
        <ResponsiveDialog title={isMobile ? "Mofiica prezzo" : <TabHeader tabValue={tabValue}
                                                                          handleTabChange={handleTabChange}/>}
                          maxWidth="xs"
                          isOpen
                          showApply
                          applyLabel="Salva"
                          onApply={formik.handleSubmit}
                          onClose={onClose}
                          isLoading={mutation.isLoading}>
            {
                isMobile && <TabHeader tabValue={tabValue} handleTabChange={handleTabChange}/>
            }
            <TabPanel value={BASE_TAB} sx={{p: 0}}>
                <TabBasePrice formik={formik}
                              basePeople={rate.basePeople}
                              ratesViewMode={ratesViewMode}
                              onRatesViewModeChange={onRatesViewModeChange}
                              dateDisabled={dateDisabled}
                />
            </TabPanel>

            <TabPanel value={OCCUPANCY_TAB} sx={{p: 0}}>
                <TabOccupancy formik={formik}
                              ratesViewMode={ratesViewMode}
                              basePeople={rate.basePeople}
                              maxOccupancy={rate.maxPeople}
                              minOccupancy={formik.values.minPeople > rate.minPeople ? formik.values.minPeople : rate.minPeople}
                />
            </TabPanel>

            <TabPanel value={ARRIVAL_DEPARTURE_TAB} sx={{p: 0}}>
                <TabArrivalDeparture formik={formik} />
            </TabPanel>

            <TabPanel value={NIGHTS_TAB} sx={{p: 0}}>
                <TabMinNights formik={formik}/>
            </TabPanel>
        </ResponsiveDialog>
    </TabContext>
}


const TabHeader = ({tabValue, handleTabChange}) => {
    return <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="rate tabs" variant="fullWidth">
            {
                tabs.map((tab) => <Tab key={tab.value} value={tab.value} icon={tab.icon} aria-label={tab.label}/>)
            }
        </Tabs>
    </Box>
}

export default FormRate;